import { wupoServerUri } from '../Api';
import { cleanLlmTextDeep } from './llmTextSanitizer';

/**
 * Thin client for NL dashboards (backend DashboardController at /api/auth/dashboards).
 *
 * Every call resolves to `{ ok, status, data }` so callers can branch on 409
 * (stale base version → reload) and 422 (TENANT share blocked by non-aggregated
 * widgets) without try/catch. Network failures resolve to `{ ok:false, status:0 }`.
 *
 * HITL approve/answer stay on the shared HitlController surface
 * (`/api/auth/reports/pipeline-approve|answer/{id}`), exactly as chat does.
 *
 * MOCK MODE (off by default): set `REACT_APP_DASHBOARD_MOCK=true` at build time,
 * or run `localStorage.setItem('dashboardMock','true')` in the browser and reload.
 * The mock serves one dashboard with 3 widgets and real-looking `figure_json`,
 * applies UX EditOps, versions and reverts in memory. Also in mock mode,
 * `localStorage.setItem('dashboardMockSidecarDown','true')` returns
 * `figure_json:null` (table fallback + "chart unavailable" badge).
 * Runs are simulated too (advanced on every pipeline-status poll):
 *  - generate → planning → DASHBOARD_PLAN checkpoint → approve → widgets build one by one
 *    (w_churn FAILS with a status_note) → COMPLETED with a new version. Answering re-plans.
 *  - NL edit: "make revenue weekly" / "add a breakdown by region" → EDIT_PLAN diff → approve → rebuild;
 *    "show revenue as area" applies without HITL; a question ("why …?") answers via terminalNote (META);
 *    a vague request ("make it better") → clarification (EDIT_PLAN with empty ops, question in
 *    diagnostics.issues; answer re-plans, approve fails the turn); text containing "invalid" → 422;
 *    a stale base → 409 at submit, or FAILED "STALE_BASE_VERSION: …" if a UX edit lands mid-run.
 *  - pin-chat-turn appends a static (non-filterable) KPI widget as a PIN version.
 */

const readLocalFlag = (key) => {
  try {
    return typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem(key) === 'true';
  } catch (e) {
    return false;
  }
};

export const DASHBOARD_MOCK_MODE =
  process.env.REACT_APP_DASHBOARD_MOCK === 'true' || readLocalFlag('dashboardMock');

/** plotly.js version bundled in the client (pinned to the line Python plotly 5.24.x ships). */
export const CLIENT_PLOTLYJS_VERSION = '2.35.2';

const BASE = () => `${wupoServerUri.devnet}/api/auth/dashboards`;

const jsonHeaders = (jwtToken) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${jwtToken}`,
});

const request = async (method, path, body, jwtToken) => {
  try {
    const response = await fetch(`${BASE()}${path}`, {
      method,
      headers: jsonHeaders(jwtToken),
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    let data = null;
    if (response.status !== 204) {
      try {
        // LLM-written text (titles, captions, answers) must never show prompt fence tags.
        data = cleanLlmTextDeep(await response.json());
      } catch (e) {
        data = null;
      }
    }
    return { ok: response.ok, status: response.status, data };
  } catch (e) {
    console.error(`dashboardApi ${method} ${path} failed:`, e);
    return { ok: false, status: 0, data: null };
  }
};

const qs = (params) => {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
  return parts.length ? `?${parts.join('&')}` : '';
};

// ── EditOp builders (shape: { op, target_widget_id?, control_id?, args }) ────

export const editOps = {
  setVizType: (widgetId, vizType) => ({ op: 'set_viz_type', target_widget_id: widgetId, args: { viz_type: vizType } }),
  setLayout: (widgetId, { x, y, w, h }) => ({ op: 'set_layout', target_widget_id: widgetId, args: { x, y, w, h } }),
  setTitle: (widgetId, title) => ({ op: 'set_title', target_widget_id: widgetId, args: { title } }),
  setControlValue: (controlId, value) => ({ op: 'set_control_value', control_id: controlId, args: { value } }),
};

/**
 * Repair options → request body. A string is the first-iteration `hint`. Every field is optional:
 * { hint, goal_override, remember, allow_questions, suggestion_id, suggestion_hint, answers: [{question_id, text}] }.
 */
const repairBody = (opts) => {
  const o = typeof opts === 'string' || opts == null ? { hint: opts || undefined } : opts;
  const out = {};
  Object.keys(o).forEach((k) => {
    const v = o[k];
    if (v === undefined || v === null || v === '') return;
    if (Array.isArray(v) && !v.length) return;
    out[k] = v;
  });
  return out;
};

// ── REST surface (§4.6) ──────────────────────────────────────────────────────

const real = {
  listDashboards: (jwt) => request('GET', '', undefined, jwt),
  createDashboard: (title, jwt) => request('POST', '', { title }, jwt),
  generateDashboard: (requestText, title, jwt) =>
    request('POST', '/generate', { request: requestText, title: title || undefined }, jwt),
  getDashboard: (id, versionId, jwt) => request('GET', `/${id}${qs({ version_id: versionId })}`, undefined, jwt),
  patchDashboard: (id, patch, jwt) => request('PATCH', `/${id}`, patch, jwt),
  deleteDashboard: (id, jwt) => request('DELETE', `/${id}`, undefined, jwt),
  getWidgetData: (id, widgetId, { versionId, controls, theme = 'light', refresh = false } = {}, jwt) =>
    request(
      'GET',
      `/${id}/widgets/${encodeURIComponent(widgetId)}/data${qs({
        version_id: versionId,
        controls: controls && Object.keys(controls).length ? JSON.stringify(controls) : undefined,
        theme,
        refresh: refresh ? 'true' : undefined,
      })}`,
      undefined,
      jwt
    ),
  getWidgetProvenance: (id, widgetId, versionId, jwt) =>
    request('GET', `/${id}/widgets/${encodeURIComponent(widgetId)}/provenance${qs({ version_id: versionId })}`, undefined, jwt),
  postUxEdits: (id, baseVersionId, ops, jwt) =>
    request('POST', `/${id}/edits`, { base_version_id: baseVersionId, input_kind: 'UX', ops }, jwt),
  postNlEdit: (id, baseVersionId, text, jwt) =>
    request('POST', `/${id}/edits`, { base_version_id: baseVersionId, input_kind: 'NL', text }, jwt),
  getRunStatus: (runId, jwt) => request('GET', `/runs/${runId}/pipeline-status`, undefined, jwt),
  postRepairWidget: (id, widgetId, baseVersionId, opts, jwt) =>
    request('POST', `/${id}/widgets/${encodeURIComponent(widgetId)}/repair`,
      { base_version_id: baseVersionId, ...repairBody(opts) }, jwt),
  diagnoseWidget: (id, widgetId, versionId, jwt) =>
    request('POST', `/${id}/widgets/${encodeURIComponent(widgetId)}/diagnose${qs({ version_id: versionId })}`, undefined, jwt),
  postAddWidget: (id, baseVersionId, description, jwt) =>
    request('POST', `/${id}/widgets`, { base_version_id: baseVersionId, description }, jwt),
  listVersions: (id, jwt) => request('GET', `/${id}/versions`, undefined, jwt),
  revertToVersion: (id, versionId, jwt) => request('POST', `/${id}/revert/${versionId}`, undefined, jwt),
  pinChatTurn: (id, chatTurnId, queryIndex, jwt) =>
    request('POST', `/${id}/pin-chat-turn`, { chat_turn_id: chatTurnId, query_index: queryIndex }, jwt),
  getCapabilities: (jwt) => request('GET', '/capabilities', undefined, jwt),
};

// ── Mock backend ─────────────────────────────────────────────────────────────

const MOCK_LATENCY_MS = 350;
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const clone = (o) => (o === undefined ? undefined : JSON.parse(JSON.stringify(o)));
const ok = (data, status = 200) => ({ ok: true, status, data });
const fail = (status, data = null) => ({ ok: false, status, data });

const MOCK_PALETTE = ['#4A3596', '#27A2AA', '#F2736A', '#C28100', '#1E385B', '#6DD47E', '#c7357c', '#4dc9f0'];
const MONTHS = ['2025-11', '2025-12', '2026-01', '2026-02', '2026-03', '2026-04', '2026-05', '2026-06',
  '2026-07', '2026-08', '2026-09', '2026-10'];
const REGIONS = ['North', 'South', 'East', 'West', 'Central'];
const REGION_FACTOR = { North: 0.27, South: 0.22, East: 0.19, West: 0.18, Central: 0.14 };

const MOCK_CAPABILITIES = {
  renderer_version: 'mock-1',
  plotly_version: '5.24.1',
  plotlyjs_version: '2.35.2',
  viz_types: [
    { viz_type: 'line', group: 'existing', shape_kinds: ['TIME_SERIES', 'CATEGORICAL_SERIES'], cardinality: { max_series: 12 } },
    { viz_type: 'area', group: 'existing', shape_kinds: ['TIME_SERIES'], cardinality: { max_series: 8 } },
    { viz_type: 'stacked_area', group: 'existing', shape_kinds: ['TIME_SERIES', 'CATEGORICAL_SERIES'], cardinality: { max_series: 8 } },
    { viz_type: 'bar', group: 'existing', shape_kinds: ['CATEGORICAL', 'CATEGORICAL_SERIES', 'TIME_SERIES'], cardinality: { max_categories: 50 } },
    { viz_type: 'stacked_bar', group: 'existing', shape_kinds: ['CATEGORICAL_SERIES', 'TIME_SERIES', 'CATEGORICAL'], cardinality: { max_categories: 50, max_series: 10 } },
    { viz_type: 'pie', group: 'existing', shape_kinds: ['CATEGORICAL'], cardinality: { max_categories: 8 } },
    { viz_type: 'histogram', group: 'existing', shape_kinds: ['DISTRIBUTION'], cardinality: {} },
    { viz_type: 'heatmap', group: 'existing', shape_kinds: ['MATRIX'], cardinality: { max_cells: 2500 } },
    { viz_type: 'treemap', group: 'existing', shape_kinds: ['HIERARCHY', 'CATEGORICAL'], cardinality: { max_categories: 200 } },
    { viz_type: 'kpi', group: 'kpi', shape_kinds: ['KPI'], cardinality: {} },
    { viz_type: 'kpi_delta', group: 'kpi', shape_kinds: ['KPI_DELTA'], cardinality: {} },
    { viz_type: 'sunburst', group: 'hierarchy', shape_kinds: ['HIERARCHY'], cardinality: { max_categories: 200 } },
    { viz_type: 'funnel', group: 'flow', shape_kinds: ['STAGES', 'CATEGORICAL'], cardinality: { max_categories: 12 } },
    { viz_type: 'waterfall', group: 'flow', shape_kinds: ['STAGES'], cardinality: { max_categories: 24 } },
    { viz_type: 'box', group: 'distribution', shape_kinds: ['DISTRIBUTION'], cardinality: {} },
    { viz_type: 'scatter', group: 'relation', shape_kinds: ['XY'], cardinality: {} },
    { viz_type: 'table', group: 'tabular', shape_kinds: ['KPI', 'KPI_DELTA', 'TIME_SERIES', 'CATEGORICAL', 'CATEGORICAL_SERIES', 'MATRIX', 'DISTRIBUTION', 'HIERARCHY', 'STAGES', 'XY', 'TABLE'], cardinality: {} },
  ].map((v) => ({ required_roles: {}, style_keys: {}, supports_gl: v.viz_type === 'scatter', ...v })),
};

const resolveMonths = (period) => {
  if (!period) return MONTHS;
  if (period.from || period.to) {
    const from = (period.from || '0000-00').slice(0, 7);
    const to = (period.to || '9999-99').slice(0, 7);
    return MONTHS.filter((m) => m >= from && m <= to);
  }
  const n = { last_3_months: 3, last_6_months: 6, last_12_months: 12, ytd: 10 }[period.relative] || 12;
  return MONTHS.slice(-n);
};

const regionFilter = (region) => {
  if (region === undefined || region === null || region === '') return REGIONS;
  const list = Array.isArray(region) ? region : [region];
  const kept = REGIONS.filter((r) => list.includes(r));
  return kept.length ? kept : REGIONS;
};

const round = (v, d = 0) => Math.round(v * 10 ** d) / 10 ** d;

const mockRows = (widgetId, controls) => {
  const months = resolveMonths(controls.period);
  const regions = regionFilter(controls.region);
  const share = regions.reduce((s, r) => s + REGION_FACTOR[r], 0);
  if (widgetId === 'w_revenue_trend') {
    return {
      columns: [
        { name: 'month', type: 'date', role: 'time' },
        { name: 'collected_revenue', type: 'number', role: 'measure' },
      ],
      rows: months.map((m, i) => ({
        month: `${m}-01`,
        collected_revenue: round((1840000 + 52000 * MONTHS.indexOf(m) + 91000 * Math.sin(i * 1.3)) * share, 2),
      })),
    };
  }
  if (widgetId === 'w_by_region') {
    const k = months.length / 12;
    return {
      columns: [
        { name: 'region', type: 'string', role: 'dimension' },
        { name: 'collected_revenue', type: 'number', role: 'measure' },
      ],
      rows: regions
        .map((r) => ({ region: r, collected_revenue: round(24600000 * REGION_FACTOR[r] * k, 2) }))
        .sort((a, b) => b.collected_revenue - a.collected_revenue),
    };
  }
  if (widgetId === 'w_churn') {
    return {
      columns: [
        { name: 'segment', type: 'string', role: 'dimension' },
        { name: 'churn_rate', type: 'number', role: 'measure' },
      ],
      rows: [{ segment: 'Retail', churn_rate: 4.2 }, { segment: 'SMB', churn_rate: 6.8 }, { segment: 'Enterprise', churn_rate: 1.9 }],
    };
  }
  if (String(widgetId).indexOf('w_added_') === 0) return mockRows('w_by_region', controls);
  // w_recovery_rate (chat pin, static: ignores controls)
  return {
    columns: [{ name: 'recovery_rate_pct', type: 'number', role: 'measure' }],
    rows: [{ recovery_rate_pct: 87.4 }],
  };
};

const baseLayout = (title) => ({
  template: { layout: { font: { family: 'Inter, Roboto, Helvetica, Arial, sans-serif', size: 12, color: '#333' } } },
  title: title ? { text: title, x: 0.01, font: { size: 13 } } : undefined,
  margin: { l: 56, r: 16, t: title ? 36 : 16, b: 44 },
  paper_bgcolor: '#ffffff',
  plot_bgcolor: '#ffffff',
  colorway: MOCK_PALETTE,
  xaxis: { gridcolor: '#eeeeee', zeroline: false },
  yaxis: { gridcolor: '#eeeeee', zeroline: false, tickformat: '~s' },
  legend: { orientation: 'h', y: -0.2 },
  separators: '.,',
  uirevision: 'dashboard',
});

/** Mimics the sidecar's /figure output for the handful of types the mock needs. */
const mockFigure = (visual, data) => {
  const vt = visual.viz_type;
  const xKey = visual.x;
  const yKey = (visual.y && visual.y[0]) || data.columns.find((c) => c.role === 'measure').name;
  const xs = data.rows.map((r) => r[xKey]);
  const ys = data.rows.map((r) => r[yKey]);
  const layout = baseLayout(null);
  switch (vt) {
    case 'line':
    case 'scatter':
      return { data: [{ type: 'scatter', mode: vt === 'line' ? 'lines+markers' : 'markers', name: yKey, x: xs, y: ys,
        line: { color: MOCK_PALETTE[0], width: 2 }, hovertemplate: '%{x}<br>%{y:,.0f}<extra></extra>' }],
      layout: { ...layout, xaxis: { ...layout.xaxis, type: xKey === 'month' ? 'date' : '-',
        rangeslider: { visible: !!(visual.style && visual.style.x_rangeslider) } } } };
    case 'area':
    case 'stacked_area':
      return { data: [{ type: 'scatter', mode: 'lines', fill: 'tozeroy', name: yKey, x: xs, y: ys,
        line: { color: MOCK_PALETTE[1] } }], layout };
    case 'bar':
    case 'stacked_bar':
    case 'waterfall':
    case 'funnel':
      if (vt === 'funnel') return { data: [{ type: 'funnel', y: xs, x: ys, marker: { color: MOCK_PALETTE[0] } }], layout };
      if (vt === 'waterfall') return { data: [{ type: 'waterfall', x: xs, y: ys, measure: xs.map(() => 'relative') }], layout };
      return { data: [{ type: 'bar', name: yKey, x: xs, y: ys, marker: { color: MOCK_PALETTE[0] },
        hovertemplate: '%{x}<br>%{y:,.0f}<extra></extra>' }], layout };
    case 'pie':
      return { data: [{ type: 'pie', labels: xs, values: ys, hole: 0.45, sort: false, marker: { colors: MOCK_PALETTE } }],
        layout: { ...layout, margin: { l: 16, r: 16, t: 16, b: 16 } } };
    case 'treemap':
      return { data: [{ type: 'treemap', labels: xs, parents: xs.map(() => ''), values: ys }],
        layout: { ...layout, margin: { l: 4, r: 4, t: 4, b: 4 } } };
    case 'kpi':
    case 'kpi_delta':
      return { data: [{ type: 'indicator', mode: vt === 'kpi_delta' ? 'number+delta' : 'number', value: ys[0],
        number: { suffix: '%', valueformat: '.1f', font: { color: MOCK_PALETTE[0], size: 48 } },
        delta: vt === 'kpi_delta' ? { reference: 84.9, valueformat: '.1f' } : undefined }],
      layout: { ...layout, margin: { l: 8, r: 8, t: 8, b: 8 } } };
    case 'table':
      return { data: [{ type: 'table',
        header: { values: data.columns.map((c) => `<b>${c.name}</b>`), fill: { color: '#f3f1fa' }, align: 'left' },
        cells: { values: data.columns.map((c) => data.rows.map((r) => r[c.name])), align: 'left' } }],
      layout: { ...layout, margin: { l: 4, r: 4, t: 4, b: 4 } } };
    default:
      return null;
  }
};

const MOCK_SPEC_V1 = {
  spec_version: 1,
  title: 'Collections performance',
  description: 'Collected revenue over the last 12 months by region.',
  global_controls: [
    { control_id: 'period', type: 'date_range', label: 'Period', param: 'date_from|date_to',
      default: { relative: 'last_12_months' }, options_source: null },
    { control_id: 'region', type: 'single_select', label: 'Region', param: 'region',
      source_column: 'main.collections.accounts.region', default: null,
      options_source: { kind: 'static', values: REGIONS } },
  ],
  widgets: [
    { widget_id: 'w_revenue_trend', kind: 'chart', title: 'Monthly collected revenue', metric_key: 'collected_revenue',
      binding_hash: '9f2c41d07a', filterable: true,
      control_bindings: { period: ['date_from', 'date_to'], region: ['region'] },
      visual: { viz_type: 'line', x: 'month', y: ['collected_revenue'], series: null, sort: { by: 'month', dir: 'asc' },
        style: { x_rangeslider: false, y_axis_type: 'linear', markers: true } },
      layout: { x: 0, y: 0, w: 8, h: 4 },
      caption: { text: 'Collected revenue rose steadily across the period.', grounded: true }, status_note: null },
    { widget_id: 'w_recovery_rate', kind: 'kpi', title: 'Recovery rate', metric_key: 'recovery_rate_pct',
      binding_hash: '1b7e90aa3c', filterable: false, control_bindings: {},
      visual: { viz_type: 'kpi', x: null, y: ['recovery_rate_pct'], series: null, sort: null, style: {} },
      layout: { x: 8, y: 0, w: 4, h: 4 }, caption: null, status_note: null },
    { widget_id: 'w_by_region', kind: 'chart', title: 'Collected revenue by region', metric_key: 'collected_revenue',
      binding_hash: '5d03be8f12', filterable: true,
      control_bindings: { period: ['date_from', 'date_to'], region: ['region'] },
      visual: { viz_type: 'bar', x: 'region', y: ['collected_revenue'], series: null,
        sort: { by: 'collected_revenue', dir: 'desc' }, style: {} },
      layout: { x: 0, y: 4, w: 12, h: 4 }, caption: null, status_note: null },
  ],
};

const nowIso = () => new Date().toISOString();

const mockState = {
  nextVersionId: 102,
  nextDashboardId: 2,
  dashboards: {
    1: {
      dashboard: { id: 1, title: 'Collections performance', description: MOCK_SPEC_V1.description,
        visibility: 'PRIVATE', status: 'ACTIVE', owner_email: 'demo@datalove.ai',
        created_at: '2026-10-20T14:02:11Z', updated_at: '2026-10-20T14:09:40Z' },
      currentVersionId: 101,
      versions: [{ id: 101, version_no: 1, created_at: '2026-10-20T14:09:40Z', change_summary: 'Generated from NL request',
        input_kind: 'NL_CREATE', spec: MOCK_SPEC_V1 }],
    },
  },
};

const mockDash = (id) => mockState.dashboards[String(id)];
const currentVersion = (d) => d.versions.find((v) => v.id === d.currentVersionId);

const pushVersion = (d, spec, summary, inputKind) => {
  const prev = currentVersion(d);
  const v = { id: mockState.nextVersionId++, version_no: prev ? prev.version_no + 1 : 1, created_at: nowIso(),
    change_summary: summary, input_kind: inputKind, spec };
  d.versions.push(v);
  d.currentVersionId = v.id;
  d.dashboard.updated_at = v.created_at;
  return v;
};

const versionPayload = (d, v) => ({ version: { id: v.id, version_no: v.version_no }, spec: clone(v.spec) });

const applyMockOps = (spec, ops) => {
  const next = clone(spec);
  const summaries = [];
  for (const op of ops) {
    const w = next.widgets.find((x) => x.widget_id === op.target_widget_id);
    switch (op.op) {
      case 'set_viz_type':
        if (!w) return { error: `Unknown widget ${op.target_widget_id}` };
        w.visual.viz_type = op.args.viz_type;
        summaries.push(`${w.title}: chart type → ${op.args.viz_type}`);
        break;
      case 'set_layout':
        if (!w) return { error: `Unknown widget ${op.target_widget_id}` };
        w.layout = { x: op.args.x, y: op.args.y, w: op.args.w, h: op.args.h };
        if (!summaries.includes('Layout updated')) summaries.push('Layout updated');
        break;
      case 'set_title':
        if (!w) return { error: `Unknown widget ${op.target_widget_id}` };
        w.title = op.args.title;
        summaries.push(`Renamed widget to "${op.args.title}"`);
        break;
      case 'set_control_value': {
        const c = next.global_controls.find((x) => x.control_id === op.control_id);
        if (!c) return { error: `Unknown control ${op.control_id}` };
        c.default = op.args.value;
        summaries.push(`Default for ${c.label || c.control_id} saved`);
        break;
      }
      default:
        return { error: `Mock does not support op ${op.op}` };
    }
  }
  return { spec: next, summary: summaries.join('; ') };
};

// ── Mock runs: NL create / NL edit / HITL (time-driven, advanced on each poll) ──

const MOCK_PLAN_MS = 1800;
const MOCK_WIDGET_STAGGER_MS = 1200;
const MOCK_WIDGET_BUILD_MS = 3200;
const MOCK_WIDGET_STAGES = ['widget_data_intent', 'widget_sql_generation', 'widget_sql_execution',
  'widget_visual_spec', 'widget_figure_compile', 'widget_caption'];
const MOCK_CHURN_NOTE = 'SQL generation could not reference a churn flag';
const MOCK_CHURN_SQL = 'SELECT a.segment AS segment,\n  ROUND(100.0 * AVG(CASE WHEN a.churn_flag THEN 1 ELSE 0 END), 1) AS churn_rate\n'
  + 'FROM main.collections.accounts a\nGROUP BY 1';
const MOCK_REPAIR_MS = 1500;
/** Tenant metadata the mock knows (names a repair may reference). */
const MOCK_TABLES = {
  'main.collections.payments': ['id', 'account_id', 'amount', 'status', 'paid_at'],
  'main.collections.accounts': ['id', 'region', 'segment', 'status', 'closed_at', 'due_amount', 'recovered_amount'],
};
const mockAvailable = () => Object.keys(MOCK_TABLES).map((table) => ({ table, columns: MOCK_TABLES[table].slice() }));
const MOCK_CHURN_DIAGNOSIS = {
  category: 'METRIC_NOT_IN_DATA',
  explanation: 'You asked for a churn rate, but your data has no churn flag. The accounts table does record each '
    + "account's segment, status and the date it was closed (closed_at).",
  available_data: [{ table: 'main.collections.accounts', columns: MOCK_TABLES['main.collections.accounts'].slice() }],
  suggestions: [
    { id: 's1', label: 'Closed accounts by segment', viz: 'bar', goal: 'Closed accounts by segment (bar chart)',
      reason: 'Closest to churn: accounts that have a closing date, per segment.',
      hint: 'Count accounts with main.collections.accounts.closed_at set, grouped by main.collections.accounts.segment',
      columns: ['main.collections.accounts.closed_at', 'main.collections.accounts.segment'] },
    { id: 's2', label: 'Accounts by status and segment', viz: 'stacked_bar', goal: 'Accounts by status and segment (stacked bar chart)',
      reason: 'Shows how account statuses are spread across segments.',
      hint: 'Count accounts grouped by main.collections.accounts.status and main.collections.accounts.segment',
      columns: ['main.collections.accounts.status', 'main.collections.accounts.segment'] },
  ],
  questions: [{ id: 'q1', text: 'How do you define a churned client?', placeholder: "e.g. closed_at is set, or status = 'CLOSED'" }],
  source: 'llm',
  failure_code: 'sql_generation',
};
/** Dotted names (schema.table / table.column) outside quotes that the mock metadata does not know. */
const mockUnknownRefs = (text) => {
  const stripped = String(text || '').replace(/'(?:[^']|'')*'/g, "''").replace(/"[^"]*"/g, '""');
  const refs = stripped.match(/(?<![A-Za-z0-9_.@/])[A-Za-z_][A-Za-z0-9_]+(?:\.[A-Za-z_][A-Za-z0-9_]+){1,3}(?![A-Za-z0-9_])/g) || [];
  const known = (ref) => {
    const r = ref.toLowerCase();
    return Object.keys(MOCK_TABLES).some((tbl) => {
      if (tbl === r || tbl.endsWith(`.${r}`)) return true;
      const dot = r.lastIndexOf('.');
      const tPart = r.slice(0, dot);
      return (tbl === tPart || tbl.endsWith(`.${tPart}`)) && MOCK_TABLES[tbl].includes(r.slice(dot + 1));
    });
  };
  return refs.filter((ref, i) => refs.indexOf(ref) === i && !known(ref));
};
const ADD_WIDGET_PREFIX = 'Add a new widget to this dashboard: ';

const MOCK_SQL = {
  w_revenue_trend: 'SELECT date_trunc(\'month\', p.paid_at) AS month, SUM(p.amount) AS collected_revenue\nFROM main.collections.payments p\nJOIN main.collections.accounts a ON a.id = p.account_id\nWHERE p.paid_at >= :date_from AND p.paid_at < :date_to\n  AND (:region IS NULL OR a.region = :region)\nGROUP BY 1\nORDER BY 1 ASC\nLIMIT 1000',
  w_by_region: 'SELECT a.region AS region, SUM(p.amount) AS collected_revenue\nFROM main.collections.payments p\nJOIN main.collections.accounts a ON a.id = p.account_id\nWHERE p.paid_at >= :date_from AND p.paid_at < :date_to\n  AND (:region IS NULL OR a.region = :region)\nGROUP BY 1\nORDER BY 2 DESC\nLIMIT 1000',
  default: 'SELECT ROUND(100.0 * SUM(recovered_amount) / NULLIF(SUM(due_amount), 0), 1) AS recovery_rate_pct\nFROM main.collections.accounts',
};

const mockState2 = { runs: {}, nextRunId: 301, nextCheckpointId: 5521, nextPin: 1 };

const mockPlan = (run) => ({
  title: run.title,
  objective: `Track collected revenue and its drivers — ${run.requestText}`
    + (run.answers.length ? ` (revised: ${run.answers.join('; ')})` : ''),
  global_scope: 'Last 12 months, all regions',
  global_controls: [
    { control_id: 'period', type: 'date_range', param: 'date_from|date_to',
      source_column: 'main.collections.payments.paid_at', default: { relative: 'last_12_months' } },
    { control_id: 'region', type: 'single_select', param: 'region',
      source_column: 'main.collections.accounts.region', default: null },
  ],
  widgets: [
    { widget_id: 'w_revenue_trend', kind: 'chart', title: 'Monthly collected revenue',
      goal: 'Show the monthly trend of collected revenue', required_metrics: ['collected_revenue'],
      filters: [{ filter_name: 'paid only', filter_condition: 'payment status is PAID', reason: 'collections' }],
      grain: 'month', preferred_viz: 'line', shape_contract: { kind: 'TIME_SERIES' },
      control_ids: ['period', 'region'], layout_hint: { size: 'large', priority: 1 } },
    { widget_id: 'w_recovery_rate', kind: 'kpi', title: 'Recovery rate', goal: 'Headline recovery rate',
      required_metrics: ['recovery_rate_pct'], filters: [], grain: null, preferred_viz: 'kpi',
      shape_contract: { kind: 'KPI' }, control_ids: [], layout_hint: { size: 'small', priority: 2 } },
    { widget_id: 'w_by_region', kind: 'chart', title: 'Collected revenue by region',
      goal: 'Compare collected revenue across regions', required_metrics: ['collected_revenue'], filters: [],
      grain: null, preferred_viz: 'bar', shape_contract: { kind: 'CATEGORICAL' },
      control_ids: ['period', 'region'], layout_hint: { size: 'full', priority: 3 } },
    { widget_id: 'w_churn', kind: 'chart', title: 'Churn by segment', goal: 'Show churn rate per customer segment',
      required_metrics: ['churn_rate'], filters: [], grain: null, preferred_viz: 'bar',
      shape_contract: { kind: 'CATEGORICAL' }, control_ids: ['period'], layout_hint: { size: 'medium', priority: 4 } },
  ],
});

const mockCheckpoint = (run, stage, stageOutput, diagnostics) => ({
  checkpointId: mockState2.nextCheckpointId++, status: 'WAITING_FOR_INPUT', currentStage: stage,
  sectionTitle: run.title, analysisFocus: run.requestText,
  stageOutputJson: JSON.stringify(stageOutput), diagnosticsJson: JSON.stringify(diagnostics),
  reportId: null, chatTurnId: null, chatSessionId: null,
});

const buildMs = (run) => run.buildMs || MOCK_WIDGET_BUILD_MS;

const mockProgress = (run, now) => {
  const elapsed = now - run.phaseStartedAt;
  return run.buildWidgets.map((w, i) => {
    const start = i * MOCK_WIDGET_STAGGER_MS;
    const base = { widget_id: w.widget_id, title: w.title };
    if (elapsed < start) return { ...base, stage: MOCK_WIDGET_STAGES[0], verdict: 'PENDING' };
    const frac = (elapsed - start) / buildMs(run);
    if (w.failNote && frac >= (w.failAt || 0.4)) {
      const failed = { ...base, stage: w.failStage || 'widget_sql_generation', verdict: 'FAILED', status_note: w.failNote };
      if (w.lastSql) failed.last_sql = w.lastSql;
      if (w.diagnosis) failed.diagnosis = clone(w.diagnosis);
      return failed;
    }
    if (frac >= 1) return { ...base, stage: 'widget_freeze', verdict: w.degraded ? 'DEGRADED' : 'VALID' };
    return { ...base, stage: MOCK_WIDGET_STAGES[Math.floor(frac * MOCK_WIDGET_STAGES.length)], verdict: 'RUNNING' };
  });
};

const mockBuildDone = (run, now) =>
  now - run.phaseStartedAt >= (run.buildWidgets.length - 1) * MOCK_WIDGET_STAGGER_MS + buildMs(run) + (run.buildMs ? 0 : 700);

const advanceMockRun = (run) => {
  const now = Date.now();
  if (run.phase === 'planning' && now - run.phaseStartedAt >= MOCK_PLAN_MS) {
    run.phaseStartedAt = now;
    if (run.inputKind === 'NL_CREATE') {
      run.phase = 'awaiting';
      run.checkpoint = mockCheckpoint(run, 'DASHBOARD_PLAN', mockPlan(run), run.answers.length
        ? { verdict: 'VALID', issues: [], annotations: ['widgets=4/12'] }
        : { verdict: 'DEGRADED', issues: ["w_churn: no dictionary column matches 'churn flag'"], annotations: ['widgets=4/12'] });
    } else if (run.editKind === 'clarify') {
      // Backend contract: clarification = EDIT_PLAN with EMPTY ops; the question rides in diagnostics.issues.
      run.phase = 'awaiting';
      run.checkpoint = mockCheckpoint(run, 'EDIT_PLAN', { dashboard_id: run.dashboardId, edit_turn_id: run.runId,
        user_input: run.requestText, ops: [], affected_widget_ids: [], widget_diffs: [], escalations: [], requires_sql: false },
      { verdict: 'DEGRADED', issues: [run.clarifyQuestion], annotations: ['clarification_needed'] });
    } else if (run.editKind === 'data') {
      run.phase = 'awaiting';
      run.checkpoint = mockCheckpoint(run, 'EDIT_PLAN', run.preview, { verdict: 'VALID', issues: [], annotations: [] });
    } else if (run.editKind === 'meta') {
      run.phase = 'done';
      run.terminalNote = run.metaAnswer;
    } else {
      run.phase = 'building';
    }
  }
  if (run.phase === 'building' && mockBuildDone(run, now)) {
    const res = run.commit();
    if (res.ask) {
      run.phase = 'awaiting';
      run.checkpoint = res.checkpoint;
      return;
    }
    if (res.error) {
      run.phase = 'failed';
      run.terminalStage = res.stage || (run.inputKind === 'NL_CREATE' ? 'dashboard_assembly' : 'edit_commit');
      run.terminalNote = res.error;
    } else {
      run.phase = 'done';
      run.resultVersionId = res.versionId;
    }
  }
};

const mockRunStatus = (run) => {
  const now = Date.now();
  const status = { planning: 'RUNNING', awaiting: 'AWAITING_HITL', building: 'RUNNING', done: 'COMPLETED', failed: 'FAILED' }[run.phase];
  let currentStage = null;
  let progress = [];
  if (run.phase === 'planning') currentStage = run.inputKind === 'NL_CREATE' ? 'dashboard_planning' : 'dashboard_edit_framing';
  if (run.phase === 'awaiting') currentStage = run.checkpoint.currentStage;
  if (run.phase === 'building' || run.phase === 'done' || run.phase === 'failed') {
    progress = run.phase === 'building' ? mockProgress(run, now) : mockProgress(run, Number.MAX_SAFE_INTEGER / 2);
    const running = progress.find((p) => p.verdict === 'RUNNING' || p.verdict === 'PENDING');
    currentStage = run.phase !== 'building' ? null
      : running ? running.stage : (run.inputKind === 'NL_CREATE' ? 'dashboard_assembly' : 'edit_commit');
    if (run.phase === 'done' && run.degradedNote) {
      progress = progress.map((p) => ({ ...p, verdict: 'DEGRADED', status_note: run.degradedNote }));
    }
  }
  return {
    runId: run.runId, dashboardId: run.dashboardId, inputKind: run.inputKind, status, currentStage,
    checkpoint: run.phase === 'awaiting' ? clone(run.checkpoint) : null,
    progress, resultVersionId: run.resultVersionId || null,
    terminalStage: run.terminalStage || null, terminalNote: run.terminalNote || null,
  };
};

const newMockRun = (fields) => {
  const run = { runId: mockState2.nextRunId++, phase: 'planning', phaseStartedAt: Date.now(), answers: [],
    buildWidgets: [], checkpoint: null, ...fields };
  mockState2.runs[run.runId] = run;
  return run;
};

const findRunByCheckpoint = (checkpointId) => Object.values(mockState2.runs)
  .find((r) => r.phase === 'awaiting' && r.checkpoint && String(r.checkpoint.checkpointId) === String(checkpointId));

const randomHash = () => Math.random().toString(16).slice(2, 12);

/** Widgets the NL_CREATE mock commits: the 3 demo widgets plus a FAILED one carrying its status_note. */
const mockGeneratedSpec = (title, requestText) => {
  const spec = clone(MOCK_SPEC_V1);
  spec.title = title;
  spec.description = requestText;
  spec.widgets.forEach((w) => { w.binding_hash = randomHash(); w.source = 'NL'; });
  spec.widgets.push({ widget_id: 'w_churn', kind: 'chart', title: 'Churn by segment', metric_key: 'churn_rate',
    binding_hash: null, filterable: false, control_bindings: {}, status: 'FAILED',
    visual: { viz_type: 'bar', x: 'segment', y: ['churn_rate'], series: null, sort: null, style: {} },
    layout: { x: 0, y: 8, w: 6, h: 4 }, caption: null, status_note: MOCK_CHURN_NOTE,
    build_failure: { stage: 'widget_sql_generation', message: MOCK_CHURN_NOTE, last_sql: MOCK_CHURN_SQL,
      failed_at: nowIso(), diagnosis: clone(MOCK_CHURN_DIAGNOSIS) },
    build_plan: { widget_id: 'w_churn', kind: 'chart', title: 'Churn by segment',
      goal: 'Show churn rate per customer segment', required_metrics: ['churn_rate'] } });
  return spec;
};

const VIZ_WORDS = ['bar', 'line', 'area', 'pie', 'table', 'treemap', 'kpi'];
const DATA_WORDS = /\b(week|weekly|month|monthly|quarter|region|segment|breakdown|filter|only|sum|average|avg|count|metric)\b/i;

/** Classify a mock NL edit and build its ops / preview / commit. */
const planMockEdit = (d, text) => {
  const spec = currentVersion(d).spec;
  const lower = text.toLowerCase();
  if (text.indexOf(ADD_WIDGET_PREFIX) === 0) {
    const goal = text.slice(ADD_WIDGET_PREFIX.length).trim();
    const ops = [{ op: 'add_widget', args: { goal, required_metrics: ['collected_revenue'], preferred_viz: 'bar' } }];
    const preview = {
      dashboard_id: d.dashboard.id, base_version_id: d.currentVersionId, user_input: text, ops, affected_widget_ids: [],
      widget_diffs: [{ widget_id: null, title: null, summary: `add widget: ${goal}`, before_sql: null, after_sql: null }],
      escalations: [], requires_sql: true,
    };
    return { editKind: 'data', add: true, goal, ops, preview, target: { widget_id: 'w_added', title: goal.slice(0, 80) } };
  }
  if (!spec.widgets.length) {
    return { editKind: 'meta', metaAnswer: 'This dashboard has no widgets yet, so there is nothing to change.' };
  }
  if (/^\s*(what|why|how|explain|qu[eé]|por qu[eé]|c[oó]mo)\b/i.test(text) || /\?\s*$/.test(text)) {
    const w = spec.widgets[0];
    return { editKind: 'meta', metaAnswer: `"${w.title}" sums payments.amount per month over the selected period; `
      + 'the region filter restricts accounts.region. Open "How I got this" on the widget for the exact SQL.' };
  }
  const specific = DATA_WORDS.test(text) || VIZ_WORDS.some((v) => new RegExp(`\\b${v}\\b`, 'i').test(text));
  if (!specific && (/\b(better|nicer|improve|fix|clean ?up)\b/i.test(text) || text.split(/\s+/).length < 2)) {
    return { editKind: 'clarify', question: `What exactly should change in "${text}"? For example the time grain (weekly / monthly), `
      + 'a breakdown (by region), or the chart type.' };
  }
  const pick = (id) => spec.widgets.find((w) => w.widget_id === id);
  let target = null;
  if (/recovery|kpi/.test(lower)) target = pick('w_recovery_rate');
  if (!target && /region/.test(lower) && !/revenue|trend|month|week/.test(lower)) target = pick('w_by_region');
  if (!target) target = pick('w_revenue_trend') || spec.widgets.find((w) => w.kind === 'chart') || spec.widgets[0];
  const viz = VIZ_WORDS.find((v) => new RegExp(`\\b${v}\\b`).test(lower));
  if (viz && !DATA_WORDS.test(lower)) {
    const ops = [editOps.setVizType(target.widget_id, viz)];
    return { editKind: 'presentation', ops, target };
  }
  const ops = [];
  if (/week/.test(lower)) ops.push({ op: 'change_grain', target_widget_id: target.widget_id, args: { grain: 'week' } });
  else if (/month/.test(lower)) ops.push({ op: 'change_grain', target_widget_id: target.widget_id, args: { grain: 'month' } });
  if (/region/.test(lower) && target.widget_id !== 'w_by_region') {
    ops.push({ op: 'add_dimension', target_widget_id: target.widget_id, args: { column: 'main.collections.accounts.region' } });
  }
  if (!ops.length) ops.push({ op: 'add_widget_filter', target_widget_id: target.widget_id, args: { condition: text } });
  const beforeSql = MOCK_SQL[target.widget_id] || MOCK_SQL.default;
  const grainOp = ops.find((o) => o.op === 'change_grain');
  const dimOp = ops.find((o) => o.op === 'add_dimension');
  let afterSql = beforeSql;
  if (grainOp) afterSql = afterSql.replace(/'month'/g, `'${grainOp.args.grain}'`).replace(/AS month/, `AS ${grainOp.args.grain}`);
  if (dimOp) afterSql = afterSql.replace(/ AS collected_revenue/, ' AS collected_revenue, a.region AS region').replace('GROUP BY 1', 'GROUP BY 1, 3');
  const filterOp = ops.find((o) => o.op === 'add_widget_filter');
  if (filterOp) afterSql = afterSql.replace('WHERE ', `WHERE /* ${text} */ `);
  const summary = ops.map((o) => (o.op === 'change_grain' ? `grain → ${o.args.grain}`
    : o.op === 'add_dimension' ? 'add dimension region' : `filter: ${text}`)).join('; ');
  const details = [];
  if (grainOp || dimOp) details.push(`GROUP BY changed${grainOp ? ` (grain ${grainOp.args.grain})` : ''}${dimOp ? ', + a.region' : ''}`);
  if (filterOp) details.push(`WHERE: + condition for "${text}"`);
  const preview = {
    dashboard_id: d.dashboard.id, base_version_id: d.currentVersionId, user_input: text, ops,
    affected_widget_ids: [target.widget_id],
    widget_diffs: [{ widget_id: target.widget_id, title: target.title, summary, before_sql: beforeSql, after_sql: afterSql,
      binding_diff: { tables_added: [], tables_removed: [], predicates_changed: !!filterOp,
        group_by_changed: !!(grainOp || dimOp), aggregations_changed: false, parameters_changed: false, details } }],
    escalations: [], requires_sql: true,
  };
  return { editKind: 'data', ops, target, preview, summary };
};

/** (Re)plans a mock NL edit run from its text — also used after a clarification answer. */
const configureMockEditRun = (run, id, baseVersionId, text) => {
  const plan = planMockEdit(mockDash(id), text);
  Object.assign(run, { requestText: text, editKind: plan.editKind, metaAnswer: plan.metaAnswer,
    clarifyQuestion: plan.question || null, baseVersionId,
    preview: plan.preview ? { ...plan.preview, edit_turn_id: run.runId } : null,
    buildWidgets: plan.target ? [{ widget_id: plan.target.widget_id, title: plan.target.title }] : [] });
  run.commit = () => {
    const dd = mockDash(id);
    if (!dd) return { error: 'Dashboard was deleted' };
    if (String(dd.currentVersionId) !== String(baseVersionId)) {
      return { error: 'STALE_BASE_VERSION: the dashboard changed while the edit was running' };
    }
    if (plan.editKind === 'presentation') {
      const res = applyMockOps(currentVersion(dd).spec, plan.ops);
      if (res.error) return { error: res.error };
      return { versionId: pushVersion(dd, res.spec, res.summary, 'NL_EDIT').id };
    }
    const next = clone(currentVersion(dd).spec);
    if (plan.add) {
      const bottom = next.widgets.reduce((m, x) => Math.max(m, ((x.layout && x.layout.y) || 0) + ((x.layout && x.layout.h) || 0)), 0);
      const wid = `w_added_${mockState2.nextPin++}`;
      next.widgets.push({ widget_id: wid, kind: 'chart', title: plan.target.title, metric_key: 'collected_revenue',
        binding_hash: randomHash(), filterable: true, control_bindings: { period: ['date_from', 'date_to'], region: ['region'] },
        visual: { viz_type: 'bar', x: 'region', y: ['collected_revenue'], series: null, sort: null, style: {} },
        layout: { x: 0, y: bottom, w: 6, h: 4 }, caption: null, status_note: null,
        build_plan: { widget_id: wid, kind: 'chart', title: plan.target.title, goal: plan.goal } });
      return { versionId: pushVersion(dd, next, `add_widget ${wid}`, 'NL_EDIT').id };
    }
    const w = next.widgets.find((x) => x.widget_id === plan.target.widget_id);
    if (!w) return { error: 'Target widget no longer exists' };
    w.binding_hash = randomHash();
    w.caption = { text: `Rebuilt by an edit: ${plan.summary}.`, grounded: true };
    return { versionId: pushVersion(dd, next, `${w.title}: ${plan.summary}`, 'NL_EDIT').id };
  };
};

const mock = {
  listDashboards: async () => {
    await delay(MOCK_LATENCY_MS);
    return ok(Object.values(mockState.dashboards).map((d) => ({
      id: d.dashboard.id, title: d.dashboard.title, visibility: d.dashboard.visibility,
      updated_at: d.dashboard.updated_at, widget_count: currentVersion(d) ? currentVersion(d).spec.widgets.length : 0,
      owner_email: d.dashboard.owner_email,
    })));
  },
  createDashboard: async (title) => {
    await delay(MOCK_LATENCY_MS);
    const id = mockState.nextDashboardId++;
    const v = { id: mockState.nextVersionId++, version_no: 1, created_at: nowIso(), change_summary: 'Created',
      input_kind: 'UX', spec: { spec_version: 1, title, global_controls: [], widgets: [] } };
    mockState.dashboards[id] = {
      dashboard: { id, title, visibility: 'PRIVATE', status: 'ACTIVE', owner_email: 'demo@datalove.ai',
        created_at: v.created_at, updated_at: v.created_at },
      currentVersionId: v.id, versions: [v],
    };
    return ok({ ...clone(mockState.dashboards[id].dashboard), current_version_id: v.id }, 201);
  },
  generateDashboard: async (requestText, title) => {
    await delay(MOCK_LATENCY_MS);
    const text = String(requestText || '').trim();
    if (!text) return fail(400, { message: 'request is required' });
    const finalTitle = (title && title.trim()) || (text.length > 48 ? `${text.slice(0, 45)}…` : text);
    // Like the backend: the dashboard row exists but has NO version until the run completes.
    const dashboardId = mockState.nextDashboardId++;
    mockState.dashboards[dashboardId] = {
      dashboard: { id: dashboardId, title: finalTitle, visibility: 'PRIVATE', status: 'ACTIVE', owner_email: 'demo@datalove.ai',
        created_at: nowIso(), updated_at: nowIso() },
      currentVersionId: null, versions: [],
    };
    const run = newMockRun({ dashboardId, inputKind: 'NL_CREATE', title: finalTitle, requestText: text });
    run.buildWidgets = mockPlan(run).widgets.map((w) => ({ widget_id: w.widget_id, title: w.title,
      failNote: w.widget_id === 'w_churn' ? MOCK_CHURN_NOTE : null }));
    run.commit = () => {
      const d = mockDash(dashboardId);
      if (!d) return { error: 'Dashboard was deleted' };
      const v = pushVersion(d, mockGeneratedSpec(finalTitle, text), 'Generated from NL request', 'NL_CREATE');
      d.dashboard.description = text;
      return { versionId: v.id };
    };
    return ok({ dashboard_id: dashboardId, run_id: run.runId });
  },
  getDashboard: async (id, versionId) => {
    await delay(MOCK_LATENCY_MS);
    const d = mockDash(id);
    if (!d) return fail(404);
    const v = versionId ? d.versions.find((x) => String(x.id) === String(versionId)) : currentVersion(d);
    if (!v) return fail(404);
    return ok({ dashboard: { ...clone(d.dashboard), current_version_id: d.currentVersionId },
      version: { id: v.id, version_no: v.version_no }, spec: clone(v.spec), can_edit: true });
  },
  patchDashboard: async (id, patch) => {
    await delay(MOCK_LATENCY_MS);
    const d = mockDash(id);
    if (!d) return fail(404);
    if (patch.title !== undefined) d.dashboard.title = patch.title;
    if (patch.visibility !== undefined) d.dashboard.visibility = patch.visibility;
    d.dashboard.updated_at = nowIso();
    return ok({ ...clone(d.dashboard), current_version_id: d.currentVersionId });
  },
  deleteDashboard: async (id) => {
    await delay(MOCK_LATENCY_MS);
    delete mockState.dashboards[String(id)];
    return ok(null, 204);
  },
  getWidgetData: async (id, widgetId, { versionId, controls = {}, theme = 'light' } = {}) => {
    await delay(MOCK_LATENCY_MS + Math.floor(Math.random() * 900));
    const d = mockDash(id);
    if (!d) return fail(404);
    const v = versionId ? d.versions.find((x) => String(x.id) === String(versionId)) : currentVersion(d);
    const w = v && v.spec.widgets.find((x) => x.widget_id === widgetId);
    if (!w) return fail(404);
    const effective = {};
    (v.spec.global_controls || []).forEach((c) => {
      const val = controls[c.control_id] !== undefined ? controls[c.control_id] : c.default;
      if (w.filterable && w.control_bindings && w.control_bindings[c.control_id]) effective[c.control_id] = val;
    });
    if (w.status === 'FAILED') {
      return ok({ widget_id: widgetId, status: 'ERROR', columns: [], rows: [], figure_json: null, warnings: [],
        error: w.status_note || 'Widget failed to build', binding_status: 'FAILED', filterable: false, from_cache: false });
    }
    const data = mockRows(widgetId, effective);
    const sidecarDown = readLocalFlag('dashboardMockSidecarDown');
    const figure = sidecarDown ? null : mockFigure(w.visual, data);
    return ok({
      widget_id: widgetId, status: 'OK', columns: data.columns, rows: data.rows,
      figure_json: figure,
      warnings: figure ? [] : (sidecarDown ? ['chart_unavailable'] : [`Mock cannot render ${w.visual.viz_type}`]),
      figure_error: figure ? null : (sidecarDown ? 'figure renderer unavailable (mock sidecar down)' : null),
      binding_status: 'ACTIVE', filterable: !!w.filterable, from_cache: Math.random() < 0.5, theme,
    });
  },
  getWidgetProvenance: async (id, widgetId, versionId) => {
    await delay(MOCK_LATENCY_MS);
    const d = mockDash(id);
    if (!d) return fail(404);
    const v = versionId ? d.versions.find((x) => String(x.id) === String(versionId)) : currentVersion(d);
    const w = v && v.spec.widgets.find((x) => x.widget_id === widgetId);
    if (!w) return fail(404);
    const pinned = w.source === 'CHAT_PIN' || widgetId.indexOf('w_pinned_') === 0;
    const sql = MOCK_SQL[widgetId] || MOCK_SQL.default;
    const dynamic = !pinned && !!MOCK_SQL[widgetId];
    const ok3 = { verdict: 'VALID', issues: [] };
    return ok({
      widget_id: widgetId, binding_hash: w.binding_hash,
      intent: { section_id: widgetId, metrics: [{ metric_name: w.metric_key || 'recovery_rate_pct' }] },
      sql_template: sql,
      parameters: dynamic ? [
        { name: 'date_from', type: 'DATE', column: 'main.collections.payments.paid_at', required: true },
        { name: 'date_to', type: 'DATE', column: 'main.collections.payments.paid_at', required: true },
        { name: 'region', type: 'STRING', column: 'main.collections.accounts.region', required: false },
      ] : [],
      tables: dynamic ? ['main.collections.payments', 'main.collections.accounts'] : ['main.collections.accounts'],
      verdicts: { widget_data_intent: ok3,
        widget_sql_generation: widgetId === 'w_revenue_trend' ? { verdict: 'DEGRADED', issues: ['retried: missing ORDER BY'] } : ok3,
        widget_sql_execution: ok3 },
      metric_definition: dynamic
        ? { aggregation: 'sum', measure_column: 'main.collections.payments.amount', base_filters: [], grain: widgetId === 'w_revenue_trend' ? 'month' : null }
        : { aggregation: 'ratio', measure_column: 'main.collections.accounts.recovered_amount', base_filters: [], grain: null },
      source: pinned ? 'CHAT_PIN' : (w.source || 'NL'), version_no: v.version_no,
    });
  },
  postUxEdits: async (id, baseVersionId, ops) => {
    await delay(MOCK_LATENCY_MS);
    const d = mockDash(id);
    if (!d || !currentVersion(d)) return fail(404);
    if (String(baseVersionId) !== String(d.currentVersionId)) {
      return fail(409, { message: 'Base version is stale', current_version_id: d.currentVersionId });
    }
    const res = applyMockOps(currentVersion(d).spec, ops);
    if (res.error) return fail(422, { error: 'INVALID_EDIT', message: res.error });
    const v = pushVersion(d, res.spec, res.summary, 'UX');
    return ok(versionPayload(d, v));
  },
  postNlEdit: async (id, baseVersionId, text) => {
    await delay(MOCK_LATENCY_MS);
    const d = mockDash(id);
    if (!d || !currentVersion(d)) return fail(404);
    if (String(baseVersionId) !== String(d.currentVersionId)) {
      return fail(409, { error: 'STALE_BASE_VERSION', message: 'Base version is stale', current_version_id: d.currentVersionId });
    }
    const input = String(text || '').trim();
    if (!input) return fail(400, { message: 'text is required' });
    if (/\b(invalid|reject)\b/i.test(input)) {
      return fail(422, { error: 'INVALID_EDIT', message: 'The visual validator rejected this change: a pie chart cannot show a time series.' });
    }
    const run = newMockRun({ dashboardId: d.dashboard.id, inputKind: 'NL_EDIT', title: d.dashboard.title });
    configureMockEditRun(run, id, baseVersionId, input);
    return ok({ run_id: run.runId });
  },
  /**
   * Mock repair, mirroring the backend: all parts (hint, picked suggestion — possibly edited — answers,
   * goal_override) are combined; remember=true stores them as dashboard knowledge. Dotted names the
   * metadata does not know → FAILED with a diagnosis. A FAILED widget retried with no knowledge pauses
   * at an EDIT_PLAN clarification (allow_questions) or fails with its diagnosis. A hint naming a
   * "nonexistent" table fails at execution (TECHNICAL); "degrad" completes DEGRADED; otherwise completes.
   */
  postRepairWidget: async (id, widgetId, baseVersionId, opts) => {
    await delay(MOCK_LATENCY_MS);
    const d = mockDash(id);
    if (!d || !currentVersion(d)) return fail(404, { error: 'NOT_FOUND', message: 'Dashboard not found' });
    if (String(baseVersionId) !== String(d.currentVersionId)) {
      return fail(409, { error: 'STALE_BASE_VERSION', message: 'Base version is stale', current_version_id: d.currentVersionId });
    }
    const target = currentVersion(d).spec.widgets.find((w) => w.widget_id === widgetId);
    if (!target) return fail(404, { error: 'NOT_FOUND', message: `Widget not found: ${widgetId}` });
    const o = repairBody(opts);
    const trim = (v) => String(v || '').trim();
    const hint = trim(o.hint);
    const goal = trim(o.goal_override);
    if (hint.length > 2000 || trim(o.suggestion_hint).length > 2000 || goal.length > 1000) {
      return fail(400, { error: 'INVALID_REQUEST', message: 'hint is too long (max 2000 characters)' });
    }
    const diagnosis = (d.lastRepairDiagnosis && d.lastRepairDiagnosis[widgetId])
      || (target.build_failure && target.build_failure.diagnosis) || null;
    let suggestionHint = trim(o.suggestion_hint);
    if (o.suggestion_id) {
      const sug = diagnosis && (diagnosis.suggestions || []).find((x) => x.id === o.suggestion_id);
      if (!sug && !suggestionHint) {
        return fail(400, { error: 'INVALID_REQUEST', message: `suggestion_id ${o.suggestion_id} is not in the widget's diagnosis` });
      }
      if (!suggestionHint) suggestionHint = sug.hint;
    }
    const answers = (o.answers || []).filter((a) => a && trim(a.text)).map((a) => ({
      question_id: a.question_id, text: trim(a.text),
      question: ((diagnosis && diagnosis.questions) || []).filter((q) => q.id === a.question_id).map((q) => q.text)[0] || null,
    }));
    const remember = o.remember !== false;
    const allowQuestions = o.allow_questions !== false;
    const parts = [];
    if (hint) parts.push(hint);
    answers.forEach((a) => parts.push(`${a.question ? `${a.question} → ` : ''}${a.text}`));
    if (suggestionHint) parts.push(`Chosen approach: ${suggestionHint}`);
    if (goal) parts.push(`Widget goal: ${goal}`);
    const knowledge = parts.join('\n');
    if (remember && knowledge) {
      d.knowledge = (d.knowledge || []).concat([{ stage: 'WIDGET_REPAIR', context: `Repair of widget "${target.title}"`, knowledge }]);
    }
    const wasFailed = !target.binding_hash;
    const unknown = mockUnknownRefs([hint, goal, suggestionHint].concat(answers.map((a) => a.text)).join('\n'));
    let failure = null;
    let askFirst = false;
    if (unknown.length) {
      failure = { stage: 'widget_repair', note: `Not found in your data (or not authorized): ${unknown.join(', ')}`, failAt: 0,
        diagnosis: { category: 'METRIC_NOT_IN_DATA', source: 'fallback', failure_code: 'unknown_reference',
          available_data: mockAvailable(), suggestions: [], questions: [],
          explanation: `These names in your instructions are not tables or columns you can use in this workspace: ${unknown.join(', ')}. `
            + 'Check the spelling or pick from the data that exists: main.collections.accounts, main.collections.payments.' } };
    } else if (/nonexistent/i.test(knowledge)) {
      failure = { stage: 'widget_sql_execution', note: 'The widget query failed: [TABLE_OR_VIEW_NOT_FOUND] main.collections.nonexistent',
        lastSql: 'SELECT COUNT(*) AS n FROM main.collections.nonexistent',
        diagnosis: { category: 'TECHNICAL', source: 'llm', failure_code: 'object_not_found',
          available_data: mockAvailable(), suggestions: [], questions: [],
          explanation: 'The query referenced a table that does not exist, so it could not run. Retry, or name one of the tables below.' } };
    } else if (wasFailed && !knowledge) {
      if (allowQuestions && diagnosis && (diagnosis.questions || []).length) askFirst = true;
      else {
        const bf = target.build_failure || {};
        failure = { stage: bf.stage || 'widget_sql_generation', note: bf.message || target.status_note || 'widget build failed',
          lastSql: bf.last_sql || null, diagnosis: diagnosis || null };
      }
    }
    const run = newMockRun({ dashboardId: d.dashboard.id, inputKind: 'WIDGET_REPAIR', title: target.title,
      requestText: knowledge, baseVersionId, phase: 'building', buildMs: MOCK_REPAIR_MS, repairAnswers: [], remember,
      degradedNote: !failure && /degrad/i.test(knowledge) ? 'The query returned no rows for the default filters' : null,
      buildWidgets: [{ widget_id: widgetId, title: target.title, failNote: failure && failure.note,
        failStage: failure && failure.stage, lastSql: failure && failure.lastSql, diagnosis: failure && failure.diagnosis,
        failAt: failure && failure.failAt !== undefined ? failure.failAt : 0.6 }] });
    run.repairDiagnosis = diagnosis;
    run.commit = () => {
      const dd = mockDash(id);
      if (String(dd.currentVersionId) !== String(baseVersionId)) {
        return { error: 'STALE_BASE_VERSION: the dashboard changed while the repair was running', stage: 'edit_commit' };
      }
      if (failure) {
        dd.lastRepairDiagnosis = { ...(dd.lastRepairDiagnosis || {}), [widgetId]: failure.diagnosis || diagnosis };
        return { error: `${widgetId}: ${failure.note}`, stage: failure.stage };
      }
      if (askFirst && !run.repairAnswers.length) {
        const questions = diagnosis.questions.map((q) => q.text);
        return { ask: true, checkpoint: mockCheckpoint(run, 'EDIT_PLAN', { dashboard_id: dd.dashboard.id, edit_turn_id: run.runId,
          base_version_id: baseVersionId, user_input: questions.join('\n'), ops: [], affected_widget_ids: [widgetId], requires_sql: false },
        { verdict: 'DEGRADED', issues: questions, annotations: ['clarification_needed', 'widget_repair'], diagnosis, widget_id: widgetId }) };
      }
      const next = clone(currentVersion(dd).spec);
      const w = next.widgets.find((x) => x.widget_id === widgetId);
      if (!w) return { error: 'Target widget no longer exists', stage: 'widget_repair' };
      const used = parts.concat(run.repairAnswers);
      w.binding_hash = randomHash();
      w.status = undefined;
      delete w.build_failure;
      w.filterable = false;
      w.status_note = run.degradedNote;
      if (goal) {
        w.title = goal.slice(0, 80);
        w.build_plan = { ...(w.build_plan || {}), widget_id: widgetId, goal };
      }
      w.caption = { text: used.length ? `Rebuilt with your knowledge: ${used.join(' · ')}` : 'Rebuilt.', grounded: true };
      if (dd.lastRepairDiagnosis) delete dd.lastRepairDiagnosis[widgetId];
      return { versionId: pushVersion(dd, next, `repair_widget ${widgetId}${used.length ? ' (with user knowledge)' : ' (retry)'}`,
        'WIDGET_REPAIR').id };
    };
    run.terminalNote = null;
    return ok({ run_id: run.runId });
  },
  diagnoseWidget: async (id, widgetId, versionId) => {
    await delay(MOCK_LATENCY_MS);
    const d = mockDash(id);
    const v = d && (versionId ? d.versions.find((x) => String(x.id) === String(versionId)) : currentVersion(d));
    const w = v && v.spec.widgets.find((x) => x.widget_id === widgetId);
    if (!w) return fail(404, { error: 'NOT_FOUND', message: `Widget not found: ${widgetId}` });
    const stored = (d.lastRepairDiagnosis && d.lastRepairDiagnosis[widgetId]) || (w.build_failure && w.build_failure.diagnosis)
      || (d.diagnoses && d.diagnoses[`${v.id}:${widgetId}`]);
    if (stored) return ok({ widget_id: widgetId, version_id: v.id, diagnosis: clone(stored), cached: true });
    const diagnosis = { category: w.binding_hash ? 'AMBIGUOUS_DEFINITION' : 'METRIC_NOT_IN_DATA', source: 'llm',
      failure_code: w.binding_hash ? 'sql_execution' : 'sql_generation',
      explanation: `"${w.title}" could not be built as asked. Related data exists in main.collections.accounts and main.collections.payments.`,
      available_data: mockAvailable(),
      suggestions: [{ id: 's1', label: 'Collected amount by region', viz: 'bar', goal: 'Collected amount by region (bar chart)',
        reason: 'Uses the payment amounts and account regions you have.',
        hint: 'Sum main.collections.payments.amount grouped by main.collections.accounts.region',
        columns: ['main.collections.payments.amount', 'main.collections.accounts.region'] }],
      questions: [] };
    d.diagnoses = { ...(d.diagnoses || {}), [`${v.id}:${widgetId}`]: diagnosis };
    return ok({ widget_id: widgetId, version_id: v.id, diagnosis: clone(diagnosis), cached: false });
  },
  postAddWidget: async (id, baseVersionId, description) => {
    const text = String(description || '').trim();
    if (!text) return fail(400, { error: 'INVALID_REQUEST', message: 'description is required' });
    return mock.postNlEdit(id, baseVersionId, `${ADD_WIDGET_PREFIX}${text}`);
  },
  getRunStatus: async (runId) => {
    await delay(150);
    const run = mockState2.runs[String(runId)];
    if (!run) return fail(404);
    advanceMockRun(run);
    return ok(mockRunStatus(run));
  },
  approveCheckpoint: async (checkpointId) => {
    await delay(MOCK_LATENCY_MS);
    const run = findRunByCheckpoint(checkpointId);
    if (!run) return fail(409);
    run.checkpoint = null;
    if (run.inputKind === 'WIDGET_REPAIR') { // approving a clarification without an answer fails the repair
      const w = run.buildWidgets[0];
      Object.assign(w, { failNote: 'The question was not answered; nothing was changed.', failStage: 'widget_repair_clarification',
        diagnosis: run.repairDiagnosis, failAt: 0 });
      run.phase = 'failed';
      run.terminalStage = 'widget_repair_clarification';
      run.terminalNote = `${w.widget_id}: The question was not answered; nothing was changed.`;
      return ok(null);
    }
    if (run.editKind === 'clarify') {
      run.phase = 'failed';
      run.terminalStage = 'dashboard_edit_framing';
      run.terminalNote = 'Clarification needed but no answer was given.';
      return ok(null);
    }
    run.phase = 'building';
    run.phaseStartedAt = Date.now();
    return ok(null);
  },
  submitCheckpointAnswer: async (checkpointId, answer) => {
    await delay(MOCK_LATENCY_MS);
    const run = findRunByCheckpoint(checkpointId);
    if (!run) return fail(409);
    run.checkpoint = null;
    run.answers.push(String(answer || '').trim());
    if (run.inputKind === 'WIDGET_REPAIR') { // answer → knowledge (remember) → rebuild
      const text = String(answer || '').trim();
      run.repairAnswers.push(text);
      const d = mockDash(run.dashboardId);
      if (run.remember && d) d.knowledge = (d.knowledge || []).concat([{ stage: 'WIDGET_REPAIR', context: 'Answer while repairing', knowledge: text }]);
      run.phase = 'building';
      run.phaseStartedAt = Date.now();
      return ok(null);
    }
    if (run.inputKind === 'NL_EDIT') {
      configureMockEditRun(run, run.dashboardId, run.baseVersionId, `${run.requestText} — ${String(answer || '').trim()}`);
    }
    run.phase = 'planning';
    run.phaseStartedAt = Date.now();
    return ok(null);
  },
  listVersions: async (id) => {
    await delay(MOCK_LATENCY_MS);
    const d = mockDash(id);
    if (!d) return fail(404);
    return ok(d.versions.slice().reverse().map((v) => ({ id: v.id, version_no: v.version_no, created_at: v.created_at,
      change_summary: v.change_summary, input_kind: v.input_kind })));
  },
  revertToVersion: async (id, versionId) => {
    await delay(MOCK_LATENCY_MS);
    const d = mockDash(id);
    const target = d && d.versions.find((x) => String(x.id) === String(versionId));
    if (!target) return fail(404);
    const v = pushVersion(d, clone(target.spec), `Reverted to version ${target.version_no}`, 'UX');
    return ok(versionPayload(d, v));
  },
  pinChatTurn: async (id, chatTurnId, queryIndex) => {
    await delay(MOCK_LATENCY_MS);
    const d = mockDash(id);
    if (!d || !currentVersion(d)) return fail(404);
    const next = clone(currentVersion(d).spec);
    const bottom = next.widgets.reduce((m, w) => Math.max(m, ((w.layout && w.layout.y) || 0) + ((w.layout && w.layout.h) || 0)), 0);
    const widgetId = `w_pinned_${mockState2.nextPin++}`;
    next.widgets.push({ widget_id: widgetId, kind: 'kpi', title: `Pinned answer (turn ${chatTurnId}, query ${(queryIndex || 0) + 1})`,
      metric_key: 'recovery_rate_pct', binding_hash: randomHash(), filterable: false, control_bindings: {}, source: 'CHAT_PIN',
      visual: { viz_type: 'kpi', x: null, y: ['recovery_rate_pct'], series: null, sort: null, style: {} },
      layout: { x: 0, y: bottom, w: 4, h: 4 }, caption: null, status_note: null });
    const v = pushVersion(d, next, `Pinned chat turn ${chatTurnId}`, 'PIN');
    return ok(versionPayload(d, v));
  },
  getCapabilities: async () => {
    await delay(100);
    return ok(clone(MOCK_CAPABILITIES));
  },
};

const impl = DASHBOARD_MOCK_MODE ? mock : real;

export const listDashboards = (jwt) => impl.listDashboards(jwt);
export const createDashboard = (title, jwt) => impl.createDashboard(title, jwt);
export const generateDashboard = (requestText, title, jwt) => impl.generateDashboard(requestText, title, jwt);
export const getDashboard = (id, versionId, jwt) => impl.getDashboard(id, versionId, jwt);
export const patchDashboard = (id, patch, jwt) => impl.patchDashboard(id, patch, jwt);
export const deleteDashboard = (id, jwt) => impl.deleteDashboard(id, jwt);
export const getWidgetData = (id, widgetId, opts, jwt) => impl.getWidgetData(id, widgetId, opts, jwt);
export const getWidgetProvenance = (id, widgetId, versionId, jwt) => impl.getWidgetProvenance(id, widgetId, versionId, jwt);
export const postUxEdits = (id, baseVersionId, ops, jwt) => impl.postUxEdits(id, baseVersionId, ops, jwt);
export const postNlEdit = (id, baseVersionId, text, jwt) => impl.postNlEdit(id, baseVersionId, text, jwt);
export const getRunStatus = (runId, jwt) => impl.getRunStatus(runId, jwt);
/**
 * POST /{id}/widgets/{widgetId}/repair → { run_id }. `opts` is a hint string (null = plain retry) or
 * { hint, goal_override, remember, allow_questions, suggestion_id, suggestion_hint, answers: [{question_id, text}] }.
 */
export const postRepairWidget = (id, widgetId, baseVersionId, opts, jwt) =>
  impl.postRepairWidget(id, widgetId, baseVersionId, opts, jwt);
/** POST /{id}/widgets/{widgetId}/diagnose → { widget_id, version_id, diagnosis, cached } (never a new version). */
export const diagnoseWidget = (id, widgetId, versionId, jwt) => impl.diagnoseWidget(id, widgetId, versionId, jwt);
export const knowledgeForMockDashboard = (id) => (DASHBOARD_MOCK_MODE && mockDash(id) ? clone(mockDash(id).knowledge || []) : []);
/** POST /{id}/widgets → { run_id } (an NL edit framed as add_widget; may pause at EDIT_PLAN). */
export const postAddWidget = (id, baseVersionId, description, jwt) => impl.postAddWidget(id, baseVersionId, description, jwt);
export const REPAIR_HINT_MAX = 2000;
export const listVersions = (id, jwt) => impl.listVersions(id, jwt);
export const revertToVersion = (id, versionId, jwt) => impl.revertToVersion(id, versionId, jwt);
export const pinChatTurn = (id, chatTurnId, queryIndex, jwt) => impl.pinChatTurn(id, chatTurnId, queryIndex, jwt);
export const getCapabilities = (jwt) => impl.getCapabilities(jwt);

// HITL approve/answer: shared surface, same endpoints chat uses (mock mode simulates dashboard runs).
export const approveCheckpoint = async (checkpointId, jwtToken) => {
  if (DASHBOARD_MOCK_MODE) return mock.approveCheckpoint(checkpointId);
  try {
    const response = await fetch(`${wupoServerUri.devnet}/api/auth/reports/pipeline-approve/${checkpointId}`, {
      method: 'POST',
      headers: jsonHeaders(jwtToken),
    });
    return { ok: response.ok, status: response.status, data: null };
  } catch (e) {
    return { ok: false, status: 0, data: null };
  }
};

export const submitCheckpointAnswer = async (checkpointId, answer, jwtToken) => {
  if (DASHBOARD_MOCK_MODE) return mock.submitCheckpointAnswer(checkpointId, answer);
  try {
    const response = await fetch(`${wupoServerUri.devnet}/api/auth/reports/pipeline-answer/${checkpointId}`, {
      method: 'POST',
      headers: jsonHeaders(jwtToken),
      body: JSON.stringify({ answer }),
    });
    return { ok: response.ok, status: response.status, data: null };
  } catch (e) {
    return { ok: false, status: 0, data: null };
  }
};

// ── Spec helpers shared by the viewer ────────────────────────────────────────

const normKind = (k) => (k ? String(k).trim().toUpperCase() : null);

/** The widget's result shape kind, from the spec (several spellings tolerated). */
export const widgetShapeKind = (widget) =>
  normKind(
    (widget && (widget.shape_kind ||
      (widget.shape_contract && widget.shape_contract.kind) ||
      (widget.shape && widget.shape.kind))) || null
  );

/**
 * Infer the result shape kind (N2) from the column roles of a WidgetDataResponse.
 * Returns null when the roles are ambiguous (e.g. two dimensions: MATRIX or series).
 */
export const inferShapeKind = (response) => {
  const cols = (response && Array.isArray(response.columns)) ? response.columns : [];
  if (!cols.length) return null;
  const roles = cols.map((c) => String((c && c.role) || '').toLowerCase());
  const count = (r) => roles.filter((x) => x === r).length;
  const rowCount = Array.isArray(response.rows) ? response.rows.length : (response.row_count || 0);
  const time = count('time');
  const dims = count('dimension');
  const measures = count('measure');
  if (count('stage_order') > 0) return 'STAGES';
  if (count('parent') > 0) return 'HIERARCHY';
  if (time > 0) return dims > 0 ? 'CATEGORICAL_SERIES' : 'TIME_SERIES';
  if (dims === 1) return 'CATEGORICAL';
  if (dims === 0 && measures === 1) return rowCount <= 1 ? 'KPI' : 'DISTRIBUTION';
  if (dims === 0 && measures === 2) return rowCount <= 1 ? 'KPI_DELTA' : 'XY';
  return null;
};

/**
 * Viz types offered in the chart-type menu: registry entries whose shape_kinds
 * include the widget's shape. The shape comes from the spec when present, else
 * from the data's column roles; failing both, from the shape kinds of the
 * widget's current viz type, so the menu stays valid.
 */
export const vizTypesForWidget = (capabilities, widget, response) => {
  const types = (capabilities && Array.isArray(capabilities.viz_types)) ? capabilities.viz_types : [];
  if (!types.length || !widget) return [];
  let kinds = [];
  const shape = widgetShapeKind(widget) || inferShapeKind(response);
  if (shape) kinds = [shape];
  else {
    const current = types.find((t) => t.viz_type === (widget.visual && widget.visual.viz_type));
    kinds = current ? (current.shape_kinds || []).map(normKind) : [];
  }
  if (!kinds.length) return [];
  return types.filter((t) => (t.shape_kinds || []).map(normKind).some((k) => kinds.includes(k)));
};

/**
 * Why a chart type offered for the widget's shape can't show the data it currently has, or null.
 * Mirrors the backend's deterministic chart-type change (VizTypeConversion) plus the registry's
 * cardinality limits checked against the loaded rows. Returns { key, params } for
 * `Dashboards.widget.vizUnavailable.<key>`.
 */
export const vizTypeUnavailableReason = (cap, widget, response) => {
  if (!cap || !widget) return null;
  const visual = widget.visual || {};
  const slots = cap.required_roles || {};
  const card = cap.cardinality || {};
  const cols = (response && Array.isArray(response.columns)) ? response.columns : [];
  const rows = (response && Array.isArray(response.rows)) ? response.rows : [];
  const roleOf = (name) => {
    const c = cols.find((col) => col && col.name === name);
    return c ? String(c.role || '').toLowerCase() : null;
  };
  const y = Array.isArray(visual.y) ? visual.y.filter(Boolean) : [];
  const measures = cols.filter((c) => String((c && c.role) || '').toLowerCase() === 'measure').map((c) => c.name);
  const plotted = y.length ? y : measures;
  if (cap.viz_type !== 'table') {
    if (slots.y && slots.y.max === 1 && y.length > 1) return { key: 'tooManyMeasures', params: { count: y.length } };
    if (visual.series && !slots.series && !slots.parent) return { key: 'noSeries', params: {} };
  }
  if (!rows.length) return null;
  if (card.max_rows && rows.length > card.max_rows) {
    return { key: 'tooManyRows', params: { count: rows.length, max: card.max_rows } };
  }
  if (cap.viz_type === 'table') return null;
  const distinct = (name) => new Set(rows.map((r) => JSON.stringify(r ? r[name] : null))).size;
  const isCategory = (name) => ['time', 'dimension', 'label'].includes(roleOf(name));
  const xName = (visual.x && (!cols.length || isCategory(visual.x)) && visual.x)
    || (cols.find((c) => isCategory(c.name)) || {}).name;
  if (card.max_categories && slots.x && xName) {
    const n = distinct(xName);
    if (n > card.max_categories) return { key: 'tooManyCategories', params: { count: n, max: card.max_categories } };
  }
  const seriesName = visual.series && slots.series ? visual.series : null;
  if (card.max_series && seriesName) {
    const n = distinct(seriesName);
    if (n > card.max_series) return { key: 'tooManySeries', params: { count: n, max: card.max_series } };
  }
  if (cap.viz_type === 'pie' && plotted.length) {
    const values = rows.map((r) => Number(r ? r[plotted[0]] : NaN)).filter((v) => !Number.isNaN(v));
    const total = values.reduce((s, v) => s + v, 0);
    if (values.some((v) => v < 0) || total <= 0) return { key: 'negativeValues', params: {} };
  }
  return null;
};

/** Does the widget react to a given global control? */
export const widgetUsesControl = (widget, controlId) => {
  if (!widget || widget.filterable === false) return false;
  const cb = widget.control_bindings;
  if (!cb || Object.keys(cb).length === 0) return true; // no explicit bindings → assume all controls
  return Object.prototype.hasOwnProperty.call(cb, controlId);
};
